/**
 * Composition pour le chargement des configurations de tâche
 */
import { ref } from 'vue';
/***/
import { useTaskConfigurationStore } from '@stores/taskConfiguration';
import { useTaskConfigurationGroupStore } from '@stores/taskConfigurationGroup';

export function useTaskConfiguration({}) {

    // Stores
    const groupStore = useTaskConfigurationGroupStore();
    const taskConfigurationStore = useTaskConfigurationStore();

    // Chargement des configurations de tâche
    const loadingTaskConfigurations = ref(false);
    const loadingTaskConfigurationsError = ref(false);
    const loadTaskConfiguration = (() => {
        loadingTaskConfigurations.value = true;
        return taskConfigurationStore.loadTaskConfigurationList().then(() => {
            return Promise.resolve();
        }).catch(() => {
            loadingTaskConfigurationsError.value = true;
        }).finally(() => {
            loadingTaskConfigurations.value = false;
        });
    });

    // Chargement des formats des configurations de tâche
    const loadingTaskFormats = ref(false);
    const loadingTaskFormatsError = ref(false);
    const loadTaskFormats = (() => {
        loadingTaskFormats.value = true;
        return taskConfigurationStore.loadFormatsBySocialNetwork().then(() => {
            return Promise.resolve();
        }).catch(() => {
            loadingTaskFormatsError.value = true;
        }).finally(() => {
            loadingTaskFormats.value = false;
        });
    });

    /**
     * Retourne la configuration de tâche dont le format est en paramètre
     * @param string format
     * @return object|null
     */
    const getTaskConfigurationByFormat = ((format) => {
        return taskConfigurationStore.getConfigurationByFormatType(format);
    });


    /**
     * Tri les configurations de tâche par nombre de crédits
     * @param array taskConfigurations Tableau des configurations de tâche à trier
     * @param string direction Sens de direction du tri (asc|desc)
     */
    const sortTaskConfigurationsByCredits = ((taskConfigurations, direction) => {
        return taskConfigurations.sort((format1, format2) => {
            let sortValue = 0;
            if(format1.credits > format2.credits) {
                sortValue = 1;
            } else if(format1.credits < format2.credits) {
                sortValue = -1
            }
            if(direction === 'desc') {
                sortValue *= -1;
            }
            return sortValue;
        });
    });

    // Retourne si la configuration de tâche en paramètre correspond à une vidéo ou à une animation
    const isVideoOrAnimation = ((taskConfiguration) => {
        const format = taskConfiguration.type.toLowerCase();
        return (format.indexOf('video') === 0)
    });

    // Retourne si la configuration de tâche en paramètre correspond à un album ou à un carrousel
    const isAlbumOrCarousel = ((taskConfiguration) => {
        const format = taskConfiguration.type.toLowerCase();
        return (
            format.indexOf('album') === 0 || format.indexOf('carrousel') === 0
        )
    });

    // Retourne si la configuration de tâche en paramètre accepte plusieurs fichiers de visuals
    const acceptMultipleVisualFiles = ((taskConfiguration) => {
        return isAlbumOrCarousel(taskConfiguration);
    });

    /**
     * Retourne le groupe de la configuration de tâche en paramètre
     * @param object taskConfiguration
     * @return object|null
     */
    const getGroupOfTaskConfiguration = ((taskConfiguration) => {
        if(! taskConfiguration.group) {
            return null;
        }

        return groupStore.getTaskConfigurationGroupById(taskConfiguration.group.id);
    });

    /**
     * Retourne l'icône de la configuration de la tâche en paramètre
     * @param object taskConfiguration
     * @param bool searchInGroup Vrai s'il faut retourner l'icône du groupe si celui de la configuration de la tâche n'a pas été trouvé
     * @return object|null
     */
    const getTaskConfigurationIcon = ((taskConfiguration, searchInGroup = false) => {

        if(taskConfiguration.emoticon) {
            return taskConfiguration.emoticon;
        } else if(searchInGroup && taskConfiguration.group) {
            const group = getGroupOfTaskConfiguration(taskConfiguration);
            return ((group) ? group.emoticon : null);
        }

        return null;
    });

    /**
     * Retourne le nombre total de crédits du format pour la tâche en paramètre
     * @param object task
     * @param object taskConfiguration
     * @return int
     */
    const getTotalCreditsOfTaskWithFormat = ((task, taskConfiguration) => {

        let totalCredits = 0;

        const hookConfiguration = (taskConfigurationStore.getConfigurationByFormatType('ACCROCHE') ?? {});
        const additionalHookConfiguration = (taskConfigurationStore.getConfigurationByFormatType('ACCROCHE_SUPPLEMENTAIRE') ?? {});
        /***/
        const hookReturnConfigurationFree = (taskConfigurationStore.getConfigurationByFormatType('RETOUR_ACCROCHE_X_GRATUIT') ?? {});
        const additionalHookReturnConfiguration = (taskConfigurationStore.getConfigurationByFormatType('RETOUR_ACCROCHE_X_PAYANT') ?? {});
        /***/
        const visualReturnType = (taskConfiguration.type === 'CREA_FIXE') ? 'RETOUR_CREA_FIXE_GRATUIT' : 'RETOUR_' + taskConfiguration.type;
        const visualReturnConfiguration = (taskConfigurationStore.getConfigurationByFormatType(visualReturnType) ?? {});

        const taskType = task.briefing.taskType;

        // Crédits pour l'accroche et son retour
        const withHook = ([ 'COMPLETE', 'HOOK' ].indexOf(taskType) !== -1);
        if(withHook) {
            const hookCredits = (hookConfiguration.credits ?? 0);
            const hookReturnCredits = (hookReturnConfigurationFree.credits ?? 0);
            totalCredits += (hookCredits + hookReturnCredits);
        }

        // Crédits pour le visuel et son retour
        const withVisual = ([ 'COMPLETE', 'VISUAL' ].indexOf(taskType) !== -1);
        if(withVisual) {
            const visualCredits = taskConfiguration.credits;
            const visualReturnCredits = (visualReturnConfiguration.credits ?? 0);

            totalCredits += (visualCredits + visualReturnCredits);
        }

        return totalCredits;
    });


    return {
        getTaskConfigurationIcon,
        isVideoOrAnimation,
        isAlbumOrCarousel,
        acceptMultipleVisualFiles,
        /***/
        getTaskConfigurationByFormat,
        loadingTaskFormats,
        loadingTaskFormatsError,
        loadTaskFormats,
        /***/
        loadingTaskConfigurations,
        loadingTaskConfigurationsError,
        loadTaskConfiguration,
        /***/
        sortTaskConfigurationsByCredits,
        getTotalCreditsOfTaskWithFormat
    };
};
